import React, { useState, useEffect } from 'react';
import { Layout, Card, Button, Table, Space, Modal, Typography, Popconfirm, message, Tag, Input, List } from 'antd';
import { PlusOutlined, DeleteOutlined, PhoneOutlined, ExclamationCircleOutlined, MenuOutlined } from '@ant-design/icons';
import { userApi } from '../api/apiService';
import AccountForm from '../components/AccountForm';
import GlobalNotifications from '../components/GlobalNotifications';

const { Title, Text } = Typography;
const { Content } = Layout;

const Dashboard = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [numbersModalVisible, setNumbersModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [filteredNumbers, setFilteredNumbers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [confirmingNumber, setConfirmingNumber] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    fetchAccounts();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await userApi.getAccounts();
      setAccounts(response.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      message.error('加载账户失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = () => {
    setIsModalVisible(true);
  };

  const handleAccountAdded = (newAccount) => {
    console.log('Account added callback received with data:', newAccount);
    if (newAccount && (newAccount.account_id || newAccount.accountId)) {
      // Refresh the accounts list to ensure we have the latest data
      fetchAccounts();
      // Close the modal
      setIsModalVisible(false);
    } else {
      console.error('Invalid account data received:', newAccount);
      message.warning('Account may have been added but data format was unexpected');
      // Still close the modal and refresh as a fallback
      setIsModalVisible(false);
      fetchAccounts();
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await userApi.deleteAccount(accountId);
      message.success('账户删除成功');
      setAccounts(accounts.filter(account => account.account_id !== accountId));
    } catch (error) {
      console.error('Error deleting account:', error);
      message.error('删除账户失败');
    }
  };

  const handleViewAllNumbers = (account) => {
    setSelectedAccount(account);
    setFilteredNumbers(account.subscribed_numbers || []);
    setNumbersModalVisible(true);
  };

  const showUnsubscribeConfirmation = (accountId, phoneNumber) => {
    console.log('Setting confirming number:', phoneNumber);
    setConfirmingNumber(phoneNumber);
  };

  const handleConfirmUnsubscribe = async (accountId, phoneNumber) => {
    if (!accountId || !phoneNumber) {
      message.error('账户ID或电话号码无效');
      setConfirmingNumber(null);
      return;
    }
    
    setConfirmLoading(true);
    try {
      await handleUnsubscribeNumber(accountId, phoneNumber);
    } finally {
      setConfirmLoading(false);
      setConfirmingNumber(null);
    }
  };

  const handleUnsubscribeNumber = async (accountId, phoneNumber) => {
    if (!accountId || !phoneNumber) {
      message.error('账户ID或电话号码无效');
      return;
    }
    
    console.log('handleUnsubscribeNumber called with:', { accountId, phoneNumber });
    
    // Show loading message
    const loadingMessage = message.loading('取消订阅号码中...', 0);
    
    try {
      console.log('Calling API to unsubscribe number');
      const response = await userApi.unsubscribeNumber(accountId, phoneNumber);
      console.log('API response:', response);
      
      // Close loading message
      loadingMessage();
      
      // Show success message
      message.success(`成功取消订阅 ${phoneNumber}`);
      
      // Update the local state
      if (selectedAccount) {
        console.log('Updating local state');
        const updatedNumbers = selectedAccount.subscribed_numbers.filter(num => {
          const numberStr = typeof num === 'object' ? num.number : num;
          return numberStr !== phoneNumber;
        });
        
        setSelectedAccount({
          ...selectedAccount,
          subscribed_numbers: updatedNumbers
        });
        
        setFilteredNumbers(updatedNumbers.filter(num => {
          const numberStr = typeof num === 'object' ? num.number : num;
          return numberStr.includes(searchText);
        }));
        
        // Also update the main accounts list
        setAccounts(accounts.map(acc => {
          if (acc.account_id === accountId) {
            return {
              ...acc,
              subscribed_numbers: updatedNumbers
            };
          }
          return acc;
        }));
      }
    } catch (error) {
      // Close loading message
      loadingMessage();
      
      console.error('Error unsubscribing number:', error);
      console.error('Error details:', error.response?.data);
      
      // Show detailed error message
      let errorMsg = '取消订阅号码失败';
      if (error.response && error.response.data) {
        if (error.response.data.error) {
          errorMsg = error.response.data.error;
        }
      }
      message.error(errorMsg);
    }
  };

  // Define different column sets for mobile and desktop
  const mobileColumns = [
    {
      title: '账户',
      key: 'account',
      render: record => (
        <div>
          <div style={{ marginBottom: 8 }}>
            <Tag color={record.platform === 'wephone' ? 'blue' : 'green'}>
              {record.platform}
            </Tag>
          </div>
          <div style={{ marginBottom: 4 }}>
            {record.email && <div><small>邮箱:</small> {record.email}</div>}
            {record.phone && <div><small>电话:</small> {record.phone}</div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <Button 
              type="primary"
              size="small"
              onClick={() => handleViewAllNumbers(record)}
            >
              {(record.subscribed_numbers?.length || 0) > 0 ? 
                `查看号码 (${record.subscribed_numbers?.length || 0})` : 
                '无号码'}
            </Button>
            <Popconfirm
              title="确定要删除此账户吗？"
              onConfirm={() => handleDeleteAccount(record.account_id)}
              okText="是"
              cancelText="否"
            >
              <Button 
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </div>
        </div>
      )
    }
  ];

  const desktopColumns = [
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      render: platform => (
        <Tag color={platform === 'wephone' ? 'blue' : 'green'}>
          {platform}
        </Tag>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '已订阅号码',
      dataIndex: 'subscribed_numbers',
      key: 'subscribed_numbers',
      render: (numbers, record) => {
        const totalNumbers = numbers?.length || 0;
        const displayLimit = 3; // Show only first 3 numbers
        
        return (
          <>
            {totalNumbers === 0 ? (
              <Text type="secondary">无号码</Text>
            ) : (
              <>
                {(numbers || []).slice(0, displayLimit).map((number, index) => (
                  <Tag icon={<PhoneOutlined />} key={`${number}-${index}`}>
                    {typeof number === 'object' ? number.number : number}
                  </Tag>
                ))}
                
                {totalNumbers > displayLimit && (
                  <Button 
                    type="link" 
                    onClick={() => handleViewAllNumbers(record)}
                    size="small"
                  >
                    查看全部 {totalNumbers} 个号码
                  </Button>
                )}
              </>
            )}
          </>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => {
        return (
          <Space>
            <Button 
              type="primary"
              onClick={() => handleViewAllNumbers(record)}
            >
              查看号码
            </Button>
            <Popconfirm
              title="确定要删除此账户吗？"
              onConfirm={() => handleDeleteAccount(record.account_id)}
              okText="是"
              cancelText="否"
            >
              <Button 
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Use appropriate columns based on screen size
  const columns = isMobile ? mobileColumns : desktopColumns;

  // Mobile-friendly table pagination settings
  const paginationConfig = {
    size: isMobile ? 'small' : 'default',
    pageSize: isMobile ? 10 : 20,
    showSizeChanger: !isMobile,
    responsive: true
  };

  return (
    <Content style={{ margin: '16px' }}>
      <GlobalNotifications />
      
      <Card
        title={<Title level={4}>账户管理</Title>}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddAccount}
          >
            添加账户
          </Button>
        }
      >
        <Table 
          dataSource={accounts} 
          columns={columns} 
          rowKey="account_id" 
          loading={loading}
          pagination={paginationConfig}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>
      
      <Modal
        title={`${selectedAccount?.platform || ''} 已订阅号码`}
        visible={numbersModalVisible}
        onCancel={() => setNumbersModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 700}
      >
        <Input
          placeholder="搜索号码"
          value={searchText}
          onChange={(e) => {
            const text = e.target.value;
            setSearchText(text);
            if (selectedAccount) {
              const numbers = selectedAccount.subscribed_numbers || [];
              setFilteredNumbers(numbers.filter(num => {
                const numberStr = typeof num === 'object' ? num.number : num;
                return numberStr.includes(text);
              }));
            }
          }}
          style={{ marginBottom: 16 }}
        />
        
        {filteredNumbers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Text type="secondary">无已订阅号码</Text>
          </div>
        ) : (
          <List
            dataSource={filteredNumbers}
            renderItem={(item) => {
              const number = typeof item === 'object' ? item.number : item;
              const isConfirming = confirmingNumber === number;
              
              // Get additional details if available
              const subscriptionDate = typeof item === 'object' ? item.subscribed_date : null;
              const expirationDate = typeof item === 'object' ? item.expiration_date : null;
              const status = typeof item === 'object' ? item.status : 'active';
              
              // Calculate days left if expiration date exists
              let daysLeft = null;
              if (expirationDate) {
                const expiryDate = new Date(expirationDate);
                const now = new Date();
                daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
              }
              
              return (
                <List.Item
                  style={{ border: '1px solid #f0f0f0', padding: '8px 12px', borderRadius: 4, marginBottom: 8 }}
                  actions={[
                    isConfirming ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button 
                          size="small"
                          onClick={() => setConfirmingNumber(null)}
                        >
                          取消
                        </Button>
                        <Button 
                          danger 
                          size="small"
                          loading={confirmLoading}
                          onClick={() => handleConfirmUnsubscribe(selectedAccount.account_id, number)}
                        >
                          确认
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        danger 
                        size="small"
                        onClick={() => showUnsubscribeConfirmation(selectedAccount.account_id, number)}
                      >
                        取消订阅
                      </Button>
                    )
                  ]}
                >
                  <List.Item.Meta
                    avatar={<PhoneOutlined />}
                    title={<Text>{number}</Text>}
                    description={
                      <div>
                        {/* Status indicator */}
                        <div style={{ marginBottom: 4 }}>
                          <Tag color={status === 'active' ? 'green' : 'red'}>
                            {status === 'active' ? '活跃' : (status || '未知')}
                          </Tag>
                          
                          {/* Expiration date and days left */}
                          {expirationDate && (
                            <span style={{ marginLeft: 8 }}>
                              到期: {new Date(expirationDate).toLocaleDateString()}
                              {daysLeft !== null && (
                                <Tag color={daysLeft > 0 ? 'green' : 'red'} style={{ marginLeft: 4 }}>
                                  {daysLeft > 0 ? `剩余 ${daysLeft} 天` : '已过期'}
                                </Tag>
                              )}
                            </span>
                          )}
                        </div>
                        
                        {/* Subscription date if available */}
                        {subscriptionDate && (
                          <small style={{ color: '#999' }}>
                            订阅日期: {new Date(subscriptionDate).toLocaleDateString()}
                          </small>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
      
      <AccountForm
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleAccountAdded}
      />
    </Content>
  );
};

export default Dashboard; 